import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faBell,
  faClipboardList,
  faDryer,
  faLocationDot,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from 'urql';
import { useAppContext } from '~/App';
import { BrandLogo } from '~/brand/BrandContext';
import { NavLink as BaseNavLink, NavLinkProps } from '~/components/NavLink';
// Removed numeric CountBadge in favor of a simple dot indicator for new activity
import { AccountSettings } from '~/components/users/settings/account';
import { graphql } from '~/gql';
import { useScrollTrigger } from '~/hooks/useScrollTrigger';
import { UserSettings } from '../../components/users/settings/user';
import styles from './Nav.module.css';
import { SupportLink } from './SupportLink';
import { TenantSwitcher } from './TenantSwitcher';
import { UserMenu } from './UserMenu';

// Custom hook for safe localStorage operations with proper memoization
const useLocalStorage = (key: string, initialValue: number) => {
  const [storedValue, setStoredValue] = useState<number>(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (item === null) return initialValue;
      const parsed = Number(item);
      return Number.isFinite(parsed) ? parsed : initialValue;
    } catch (error) {
      console.warn(`Failed to read localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: number | ((val: number) => number)) => {
      setStoredValue((prevValue) => {
        const valueToStore =
          value instanceof Function ? value(prevValue) : value;

        try {
          window.localStorage.setItem(key, String(valueToStore));
        } catch (error) {
          console.error(`Failed to set localStorage key "${key}":`, error);
        }
        return valueToStore;
      });
    },
    [key]
  );

  return [storedValue, setValue] as const;
};

// Minimal query for total activity count used to compute a simple "new activity" indicator
// Use the exact registered operation so typed-graphql recognizes it
const ActivityNav_TotalCount = graphql(`
  query ActivityNav_TotalCount(
    $first: Int
    $after: String
    $showMessages: Boolean
    $showStatus: Boolean
    $showTaskNotes: Boolean
    $unread: String
    $flagged: String
    $site: [ID!]
    $author: [ID!]
    $dateRange: String
    $tag: [String!]
    $query: String
  ) {
    allActivity(
      first: $first
      after: $after
      showMessages: $showMessages
      showStatus: $showStatus
      showTaskNotes: $showTaskNotes
      unread: $unread
      flagged: $flagged
      site: $site
      author: $author
      dateRange: $dateRange
      tag: $tag
      query: $query
    ) {
      totalCount
    }
  }
`);

// Returns true when there are new activities since the user's last visit to the Activity page.
// - Stores a snapshot of the last seen total in localStorage.
// - Resets snapshot whenever user is on the Activity route so the dot hides while viewing.
// - Polls lightly for freshness.
function useNewActivityIndicator() {
  const [result, refetch] = useQuery({
    query: ActivityNav_TotalCount,
    variables: {},
    requestPolicy: 'cache-and-network',
    pause: false,
  });

  const location = useLocation();
  const total = result.data?.allActivity.totalCount ?? 0;

  // Use the new useLocalStorage hook for efficient localStorage operations
  const [lastVisited, setLastVisited] = useLocalStorage(
    'activity.lastVisitedTotal',
    total
  );

  // Determine if a snapshot already exists in storage. This avoids showing
  // a dot for historical activity on the first successful fetch.
  const hasSnapshotRef = useRef<boolean>(false);
  // Only read once during component mount
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      window.localStorage &&
      hasSnapshotRef.current === false
    ) {
      hasSnapshotRef.current =
        window.localStorage.getItem('activity.lastVisitedTotal') !== null;
    }
  }, []);

  // Handle GraphQL errors gracefully
  useEffect(() => {
    if (result.error) {
      console.error('Failed to fetch activity count:', result.error);
      // Could implement retry logic or fallback behavior here
    }
  }, [result.error]);

  // Initialize snapshot on first successful fetch if none existed previously.
  useEffect(() => {
    if (!result.data) return;
    if (!hasSnapshotRef.current) {
      setLastVisited(total);
      hasSnapshotRef.current = true;
    }
  }, [result.data, total, setLastVisited]);

  // While on the activity page, continuously update the snapshot so the dot remains hidden
  useEffect(() => {
    if (location.pathname.startsWith('/activity')) {
      setLastVisited(total);
    }
  }, [location.pathname, total, setLastVisited]);

  // Lightweight polling to keep count reasonably fresh
  useEffect(() => {
    const isOnActivityRoute = location.pathname.startsWith('/activity');
    const hasNewActivity = hasSnapshotRef.current ? total > lastVisited : false;

    if (isOnActivityRoute || hasNewActivity) return;

    const interval = setInterval(() => {
      refetch({ requestPolicy: 'cache-and-network' });
    }, 20000);
    return () => clearInterval(interval);
  }, [refetch, location.pathname, total, lastVisited]);

  // Return false if there's an error to prevent showing incorrect indicators
  if (result.error) {
    return false;
  }

  return hasSnapshotRef.current ? total > lastVisited : false;
}

// Shared Activity Indicator Context to avoid duplicate queries/polling across
// Desktop and Mobile navs. Provider computes the value once.
const ActivityIndicatorContext = createContext<boolean>(false);

export const useActivityIndicator = () => useContext(ActivityIndicatorContext);

export const ActivityIndicatorProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const hasNewActivity = useNewActivityIndicator();
  return (
    <ActivityIndicatorContext.Provider value={hasNewActivity}>
      {children}
    </ActivityIndicatorContext.Provider>
  );
};

export const MainNavMobile = ({
  enableScrollDetection = true,
  threshold,
  unreadActivity: _unusedUnreadActivity,
}: {
  enableScrollDetection?: boolean;
  threshold?: number;
  unreadActivity?: number;
}) => {
  const { t } = useTranslation('nav');
  const navRef = useRef<HTMLDivElement>(null);

  const { isAuthenticated } = useAppContext();
  const location = useLocation();
  // Mark intentionally unused param to satisfy linter
  void _unusedUnreadActivity;

  const isScrollingDown = useScrollTrigger({
    enabled: enableScrollDetection,
    threshold,
  });

  // Consume shared activity indicator to avoid duplicate queries/polling
  const hasNewActivity = useActivityIndicator();

  // Expose the measured mobile nav height as a CSS variable so content can
  // subtract it from viewport height calculations on mobile screens.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const setVar = () => {
      try {
        const h = el.getBoundingClientRect().height || 0;
        document.documentElement.style.setProperty(
          '--mobile-nav-height',
          `${h}px`
        );
      } catch {
        /* empty */
      }
    };
    setVar();
    let ro: ResizeObserver | null = null;
    try {
      ro = new ResizeObserver(setVar);
      ro.observe(el);
    } catch {
      /* empty */
    }
    window.addEventListener('resize', setVar);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', setVar);
    };
  }, []);

  if (!isAuthenticated) {
    return null;
  }

  if (location.pathname.endsWith('/attribute-audit')) {
    return null;
  }

  return (
    <>
      <nav
        ref={navRef}
        className={classNames(
          styles.mobile,
          'flex justify-around',
          'w-full',
          'lg:hidden',
          'z-40 bg-surface-bright/60 backdrop-blur-md',
          'px-1 pb-5 pt-2',
          'transition-transform duration-300 ease-in-out',
          isScrollingDown ? 'translate-y-full' : ''
        )}
      >
        <MobileNavLink
          to='/activity'
          matches={['/activity']}
          /*  locked={ability.cannot('use', 'feat.activity')} */
          icon={
            <div className='relative'>
              <FontAwesomeIcon
                icon={faBell as IconProp}
                className='block h-8 w-7 lg:hidden'
              />
              {hasNewActivity && (
                /*               && !ability.cannot('use', 'feat.activity')
                 */ <div className='absolute -right-3 -top-1 z-10'>
                  <span className='block h-2.5 w-2.5 rounded-full bg-blue-80' />
                </div>
              )}
            </div>
          }
          title={t('nav:jobs.activity')}
        />
        <MobileNavLink
          title={t('job')}
          matches={['/jobs', '/job-templates', '/automations']}
          icon={
            <FontAwesomeIcon
              icon={faClipboardList as IconProp}
              className='block h-6 w-6 flex-1 lg:hidden'
            />
          }
          to='/jobs'
        ></MobileNavLink>
        <MobileNavLink
          to='/sites'
          matches={['/sites', '/site-config', '/site-locations', '/spaces']}
          icon={
            <FontAwesomeIcon
              icon={faLocationDot as IconProp}
              className='block h-6 w-6 flex-1 lg:hidden'
            />
          }
          title={t('site')}
        />
        <MobileNavLink
          to='/assets'
          matches={['/assets', '/deployments', '/asset-models']}
          className='flex flex-col items-center justify-center p-1 lg:hidden'
          /*       locked={ability.cannot('read', 'feat.assets')} */
          title={t('asset')}
          icon={
            <div className='relative h-8 w-6'>
              <FontAwesomeIcon
                icon={faDryer as IconProp}
                className='block h-8 w-6 flex-1 lg:hidden'
              />
            </div>
          }
        />

        <MobileNavLink
          to='/contacts'
          matches={['/contacts']}
          icon={
            <div className='relative h-8 w-6'>
              <FontAwesomeIcon
                icon={faUser as IconProp}
                className='block h-8 w-6 flex-1 lg:hidden'
              />
            </div>
          }
          title={t('contact')}
        />
      </nav>
    </>
  );
};

export const Desktop = () => {
  const { t } = useTranslation('nav');
  const { ability } = useAppContext();

  // Consume shared activity indicator to avoid duplicate queries/polling
  const hasNewActivity = useActivityIndicator();

  // Check if user is owner/admin
  const isOwner = ability.can('manage', 'Account');

  return (
    <nav
      className={classNames(
        styles.nav,
        'hidden h-[69px] items-center border-b border-outline-bright lg:flex'
      )}
      data-usetiful='main-nav'
      data-usetiful-permissions={isOwner ? 'admin' : 'user'}
    >
      <Link to='/' className='px-4 pl-6'>
        <BrandLogo className='my-auto h-10' />
      </Link>
      {/* {ability.can('use', 'feat.activity') && ( */}
      <DesktopNavLink to='/activity' matches={['/activity']}>
        {t('nav:jobs.activity')}
        {hasNewActivity && (
          <div className='absolute right-1 top-[22px]'>
            <span className='block h-2.5 w-2.5 rounded-full bg-blue-80' />
          </div>
        )}
      </DesktopNavLink>
      {/* )} */}
      <DesktopNavLink
        to='/jobs'
        matches={['/jobs', '/job-templates', '/automations']}
      >
        {t('job')}
      </DesktopNavLink>
      <DesktopNavLink
        to='/sites'
        matches={['/sites', '/site-config', '/site-locations', '/spaces']}
      >
        {t('site')}
      </DesktopNavLink>
      {/* Removing Items for now */}
      {/*  <DesktopNavLink
        to='/stock-on-hand'
        matches={[
          '/config',
          '/stock-movements',
          '/stock-transfers',
          '/stock-on-hand',
          '/items',
        ]}
        locked={ability.cannot('read', 'feat.items')}
      >
        {t('item')}
      </DesktopNavLink> */}
      <DesktopNavLink
        to='/assets'
        matches={['/assets', '/asset-config', '/deployments', '/asset-models']}
      >
        {t('asset')}
      </DesktopNavLink>
      <DesktopNavLink to='/contacts' matches={['/contacts']}>
        {t('contact')}
      </DesktopNavLink>
      <div className='lg:mr-auto'></div>
      {/* <DesktopNavLink to='/inbox'>
        <InboxIcon className='mr-1 h-5 w-5' /> {t('inbox')} 
      </DesktopNavLink> */}
      <div className='mr-5 flex items-center gap-4'>
        <SupportLink />
        <TenantSwitcher />
        <UserMenu />
      </div>
    </nav>
  );
};

export const MainNav = () => {
  const { isAuthenticated } = useAppContext();
  const location = useLocation();

  if (!isAuthenticated) {
    return null;
  }

  if (location.pathname.endsWith('/attribute-audit')) {
    return null;
  }

  try {
    return (
      <ActivityIndicatorProvider>
        <Desktop />
        <MainNavMobile />
        <UserSettings />
        <AccountSettings />
      </ActivityIndicatorProvider>
    );
  } catch (error) {
    console.error('Failed to render MainNav:', error);
    // Fallback to minimal navigation or error state
    return (
      <nav className='hidden h-[69px] items-center border-b border-outline-bright lg:flex'>
        <div className='px-4 pl-6 text-red-600'>
          Navigation temporarily unavailable
        </div>
      </nav>
    );
  }
};

const MobileNavLink = (props: NavLinkProps) => {
  const location = useLocation();

  const selectionIndicator = cva(
    'absolute left-0 right-0 min-h-[26px] max-w-12 h-5 m-auto rounded-full bg-brand-ghost bg-opacity-20 animate-[grow_400ms_ease-out_forwards]',
    {
      variants: {
        active: {
          true: 'block',
          false: 'hidden',
        },
      },
      defaultVariants: {
        active: false,
      },
    }
  );

  // Determine if this nav item is active
  const isActive = useMemo(() => {
    try {
      return Array.isArray(props.matches)
        ? props.matches.some((match) => location.pathname.startsWith(match))
        : location.pathname === props.to ||
            location.pathname.startsWith(props.to + '/');
    } catch (error) {
      console.error('Failed to determine navigation active state:', error);
      return false;
    }
  }, [props.matches, props.to, location.pathname]);

  try {
    return (
      <BaseNavLink
        {...props}
        data-usetiful={props.title}
        className={classNames(
          'relative flex flex-col items-center justify-center p-1',
          { 'pointer-events-none': props.locked }
        )}
      >
        <div className='relative z-10 flex w-full flex-col items-center justify-center'>
          <span className={selectionIndicator({ active: isActive })} />
          {props.icon}
        </div>
        <span className='block flex-shrink pt-1.5'>{props.title}</span>
      </BaseNavLink>
    );
  } catch (error) {
    console.error('Failed to render MobileNavLink:', error);
    // Fallback to simple link
    return (
      <Link
        to={props.to}
        className='relative flex flex-col items-center justify-center p-1 text-gray-500'
      >
        <span className='text-sm'>{props.title}</span>
      </Link>
    );
  }
};

const DesktopNavLink = (props: NavLinkProps) => {
  try {
    return (
      <BaseNavLink
        {...props}
        data-usetiful={`main-nav-link-${props.to}`}
        className={classNames(
          'flex items-center self-stretch border-b-4 border-white px-4 pt-1 text-copy-nav transition-colors duration-300 ease-in-out hover:border-brand',
          'relative',
          { 'pointer-events-none': props.locked }
        )}
      />
    );
  } catch (error) {
    console.error('Failed to render DesktopNavLink:', error);
    // Fallback to simple link
    return (
      <Link
        to={props.to}
        className='flex items-center self-stretch border-b-4 border-white px-4 pt-1 text-copy-nav text-gray-500'
      >
        {props.children}
      </Link>
    );
  }
};
